/**
 * @fileoverview The landing page's first look at the product: one frame, three
 * tabs, one per audience. Organizations and Instructors play their one-minute
 * launch film (`launch`); Students plays its introduction from the Onboarding
 * hub (`film`). Every tab links to its walkthrough on the hub, step by step,
 * and the hub keeps its own films whatever plays here. A tab may instead show
 * a `still` screenshot.
 *
 * The videos load only their poster until pressed (`preload="none"`): the
 * hero must not make every visitor download three films to see a headline.
 */

import { useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight } from "lucide-react";

import BrowserFrame from "@/components/BrowserFrame";
import Screenshot from "@/components/Screenshot";
import { formatDuration, walkthroughBySlug, walkthroughPath } from "@/lib/walkthroughs";

const launchMedia = (slug) => ({ src: `/media/launch/${slug}.mp4`, poster: `/media/launch/${slug}.jpg` });

/**
 * The launch films: a minute each, no voice, their words on screen, so they
 * carry no captions track. `description` is what each shows, in its own
 * words, for anyone who cannot see it; keep it in step with the film's copy
 * and sections. Rendered in video/ (config/org-launch.config.ts,
 * config/launch.config.ts) and published by its `npm run publish:launch-*`.
 */
export const LAUNCH_FILMS = {
  organizations: {
    slug: "hans-for-organizations",
    title: "Hans for organizations",
    seconds: 59,
    ...launchMedia("hans-for-organizations"),
    description:
      "A one-minute film with music and no narration. Track every program, class and student: all of it, in one view. " +
      "Hans, program management across every school. Your data, at a glance: the organization dashboard's sessions taught " +
      "and what needs attention, a year of sessions month by month, then its programs, instructors, students and lessons. " +
      "From portfolio to program: every program's type, instructors and status, then one program's classes, instructors " +
      "and missing notes. Every class, one calendar: who teaches what, and when, all week. Keep everyone in the loop: an " +
      "announcement to every instructor, and how many it reached. Share results across schools: a school's view of " +
      "attendance and instructors. Every program, in focus.",
  },
  instructors: {
    slug: "hans-for-instructors",
    title: "Hans for instructors",
    seconds: 59,
    ...launchMedia("hans-for-instructors"),
    description:
      "A one-minute film with music and no narration. Less time on scheduling, rosters and follow-ups; more time for the " +
      "music. Hans, lesson management end to end. Every student, in one place: the roster. Your week, at a glance: the " +
      "calendar. Students book themselves: a booking page. Type your lesson notes, or record the lesson. Teach beyond your " +
      "studio: programs looking for instructors. Focus on the music.",
  },
};

/**
 * The tabs, in order. A tab with `launch` plays that film, else its `film`
 * (a walkthrough slug), which it also links to; one with `still` would show a
 * screenshot.
 */
export const SHOWCASE_TABS = [
  {
    id: "organizations",
    label: "Organizations",
    launch: LAUNCH_FILMS.organizations,
    film: "introducing-hans-organizations",
    caption: "Publish a program, staff and schedule its classes, and keep every instructor in the loop.",
  },
  {
    id: "instructors",
    label: "Instructors",
    launch: LAUNCH_FILMS.instructors,
    film: "introducing-hans-instructors",
    caption: "Record or type a lesson, and send clear notes to the student and their parent.",
  },
  {
    id: "students",
    label: "Students",
    film: "introducing-hans-students",
    caption: "Students see every lesson their teachers send, and a parent gets every summary too.",
  },
];

export default function HeroShowcase() {
  const [active, setActive] = useState(SHOWCASE_TABS[0].id);
  const tab = SHOWCASE_TABS.find((t) => t.id === active);
  const film = tab.film ? walkthroughBySlug(tab.film) : null;
  // What plays in the frame: the tab's launch film, or its walkthrough.
  const playing = tab.launch ?? film;

  return (
    <div data-testid="hero-showcase">
      <div role="tablist" aria-label="See Hans for" className="mb-5 flex justify-center">
        <div className="inline-flex gap-1 rounded-full border border-[var(--studio-divider-strong)] bg-[var(--studio-raised)] p-1">
          {SHOWCASE_TABS.map((t) => (
            <button
              key={t.id}
              type="button"
              role="tab"
              aria-selected={t.id === active}
              aria-controls={`showcase-panel-${t.id}`}
              id={`showcase-tab-${t.id}`}
              data-testid={`showcase-tab-${t.id}`}
              onClick={() => setActive(t.id)}
              className={[
                "rounded-full px-4 py-2 font-sans text-sm transition duration-200 active:scale-95 md:px-5",
                t.id === active
                  ? "bg-[var(--accent-yellow)] text-[var(--studio-base)]"
                  : "text-[var(--text-secondary)] hover:text-[var(--text-primary)]",
              ].join(" ")}
            >
              {t.label}
            </button>
          ))}
        </div>
      </div>

      <div role="tabpanel" id={`showcase-panel-${tab.id}`} aria-labelledby={`showcase-tab-${tab.id}`}>
        <BrowserFrame>
          {playing ? (
            // Keyed by film, so switching tabs never carries one film's
            // playback into the other.
            <video
              key={playing.slug}
              className="block aspect-video w-full bg-[var(--studio-base)]"
              controls
              playsInline
              preload="none"
              poster={playing.poster}
              aria-label={playing.title}
              aria-describedby={playing.description ? `showcase-description-${playing.slug}` : undefined}
              data-testid="showcase-video"
            >
              <source src={playing.src} type="video/mp4" />
              {playing.captions && <track kind="captions" src={playing.captions} srcLang="en" label="English" />}
            </video>
          ) : (
            <Screenshot src={tab.still.src} alt={tab.still.alt} eager />
          )}
        </BrowserFrame>
        {playing?.description && (
          <p id={`showcase-description-${playing.slug}`} className="sr-only" data-testid="showcase-description">
            {playing.description}
          </p>
        )}
      </div>

      <div className="mt-5 flex flex-col items-center gap-2 text-center">
        <p className="text-xs text-[var(--text-tertiary)] md:text-sm">{tab.caption}</p>
        {film && (
          <Link
            to={walkthroughPath(film.slug)}
            data-testid="showcase-walkthrough-link"
            className="inline-flex items-center gap-1 font-sans text-xs text-[var(--accent-yellow-text)] transition duration-200 hover:underline md:text-sm"
          >
            {formatDuration(film.seconds)} walkthrough, step by step
            <ArrowRight className="h-3.5 w-3.5" strokeWidth={1.5} />
          </Link>
        )}
      </div>
    </div>
  );
}
